import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Lock, AlertTriangle } from "lucide-react";
import { api } from "@/api/apiClient";
import { useAuth } from "@/lib/AuthContext";
import { useNavigate } from "react-router-dom";

export default function PasswordVerificationDialog({ open, onOpenChange, onVerified }) {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const hasPassword = user?.has_password;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      const data = await api.request('POST', '/auth/verify-password', { password });
      setPassword("");
      onVerified(data.sensitive_token, data.expires_in);
      onOpenChange(false);
    } catch (err) {
      setError(err.message || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) setPassword(""); setError(""); onOpenChange(v); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5 text-[rgb(var(--brand-accent-ink))]" />
            Verify Identity
          </DialogTitle>
        </DialogHeader>
        {hasPassword ? (
          <>
            <p className="text-sm text-gray-600">
              Re-enter your password to access sensitive staff data.
            </p>
            <form onSubmit={handleSubmit} className="space-y-4 mt-2">
              <Input
                type="password"
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoFocus
                className="h-11 rounded-xl"
              />
              {error && <p className="text-sm text-red-600">{error}</p>}
              <Button
                type="submit"
                disabled={loading || !password}
                className="w-full bg-[rgb(var(--brand-yellow))] hover:bg-[rgb(var(--brand-yellow-hover))] text-black font-semibold rounded-xl"
              >
                {loading ? "Verifying..." : "Verify"}
              </Button>
            </form>
          </>
        ) : (
          <div className="space-y-4 mt-2">
            <div className="flex items-start gap-3 p-3 bg-amber-50 border border-amber-200 rounded-xl">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                You don't have a password set on your account. You need to set a password before you can access sensitive data.
              </p>
            </div>
            <Button
              onClick={() => { onOpenChange(false); navigate("/profile/account"); }}
              className="w-full bg-[rgb(var(--brand-yellow))] hover:bg-[rgb(var(--brand-yellow-hover))] text-black font-semibold rounded-xl"
            >
              Go to Account Settings
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
